<template>
  <div>
    <div class="breadcrumbs mb-2">
      <v-breadcrumbs divider=">" :items="breadcrumbsList">
        <template v-slot:item="{ item }">
          <v-breadcrumbs-item
            :to="{ name: item.path }"
            :disabled="item.disabled"
            exact
          >
            {{ item.text }}
          </v-breadcrumbs-item>
        </template>
      </v-breadcrumbs>
    </div>
    <!-- Page title and right side buttons  --->
    <PageTitle :pageHeading="`Contacts (${totalItems})`" :rightsideButton="true" :secondaryAction="secondaryAction">
      <div class="body-2 d-flex justify-end right-side-filter">
        <FilterDrawer
          v-if="loadFilter"
          @applyFilter="applyFilter()"
        ></FilterDrawer>
      </div>

      <v-btn
        v-if="$can('create','contacts')"
        dark
        class="font-weight-bold px-2"
        link
        :to="{ name: 'create-contact' }"
        v-track="'add-new-contact'"
      >
        <span class="pl-2 pr-3"> New Contact </span>
      </v-btn>
      <template v-slot:secondaryAction>
        <v-row class="d-flex justify-end">
          <v-col class="d-flex justify-end" cols="12" sm="4" md="4">
            <v-switch color="mpBlue" @change="applyFilter" :input-value="deletedContactToggle">
             <template v-slot:label>Show Deleted Contacts</template>
           </v-switch>
           </v-col>
        </v-row>
      </template>
    </PageTitle>
    <!----- ticket list page table ---->
    <v-data-table
      id="contact-list-table"
      :loading="loading"
      :headers="headers"
      :items="contactList"
      :options.sync="pagination"
      :server-items-length="totalItems"
      :footer-props="{
        'items-per-page-options': [10, 25, 50, 75, 100],
      }"
      :mobile-breakpoint="960"
      :items-per-page="perPage"
      item-key="id"
      class="elevation-2 my-0 contact-list-table"
      :item-class="(item) => (item.email.includes('@pseudonymize.email')) ? 'text--disabled' : ''"
    >
      <template
        v-if="!$can('update','contacts') && !$can('delete','contacts')"
        v-slot:[`header.action`]="{ item }"
      />
      <template v-slot:[`item.full_name`]="{ item }">
        <router-link
        :class="[
            'text-decoration-none',
            {'text--disabled': item.email.includes('@pseudonymize.email')}
          ]"
          :to="{ name: 'contact-dashboard', params: { accountId: account.id, contactId: item.id } }"
          v-track="'contact-detail-dashboard-link'"
        >
          <span v-if="item.first_name || item.last_name"
            >{{ item.first_name | upperCaseFirst }}
            {{ item.last_name | upperCaseFirst }}</span
          >
          <span v-else> -- </span>
        </router-link>
      </template>
      <template v-slot:[`item.email`]="{ item }">
        <span v-if="item.email">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <router-link
                class="text-decoration-none"
                 v-track="'contact-detail-dashboard-link'"
                :to="{
                name: 'contact-dashboard',
                params: { accountId: account.id, contactId: item.id },
                }"
              >
              <span
                :class="[
                  'word-break text-ellipsis',
                  {'text--disabled': item.email.includes('@pseudonymize.email')}
                ]"
                v-on="on"
              >
                {{ item.email }}
              </span>
            </router-link>
            </template>
            <span class="word-break">{{ item.email }}</span>
          </v-tooltip>
        </span>
        <span v-else> -- </span>
      </template>
      <template v-slot:[`item.company`]="{ item }">
        <span v-if="item.company">
          {{ item.company | upperCaseFirst }}
        </span>
        <span v-else> -- </span>
      </template>
      <template v-slot:[`item.phone`]="{ item }">
        <span v-if="item.phone">
          {{ item.phone }}
        </span>
        <span v-else> -- </span>
      </template>
      <template v-if="$can('update','contacts') || $can('delete','contacts')" v-slot:[`item.action`]="{ item }">
        <span
         v-if="checkPseudonymized(item)"
         @click.stop
         class="body-1"
         >
          <v-menu left bottom offset-y>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" icon v-track="'contact-actions-click'">
                <v-icon class="font-weight-bold">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list dense class="pa-0">
              <v-list-item-group>
                <v-list-item
                  v-if="$can('update','contacts')"
                  id="edit-contact-link"
                  v-track="'edit-contact-action'"
                  link
                  :to="{ name: 'edit-contact', params: { id: item.id } }"
                >
                  <v-list-item-icon class="mr-1">
                    <v-icon size="20"> mdi-pencil </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="body-1 font-weight-regular">
                      Edit Contact
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  v-if="$can('delete','contacts')"
                  @click="
                    getRowData(item);
                    deleteDialog = true;
                  "
                  v-track="'delete-contact-action'"
                >
                  <v-list-item-icon class="mr-1">
                    <v-icon size="20">delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="body-1 font-weight-regular">
                      Delete Contact
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-menu>
        </span>
      </template>
    </v-data-table>
    <!-- delete ticket dialog -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card class="pa-4">
        <v-card-title class="text-h6 font-weight-600">
          <span class="secondary--font text-capitalize"> Delete Contact </span>
        </v-card-title>
        <v-card-text class="body-2">
          <p>
            You have selected this Contact to be deleted.
            Do you wish to continue deleting this Contact?
          </p>

          <b class="pt-10"> Warning: You cannot undo this action. </b>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false" class="font-weight-bold" v-track="'cancel-delete-contact'">
            Cancel
          </v-btn>
          <v-btn dark @click="confirmDelete()" class="font-weight-bold" v-track="'confirm-delete-contact'">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import PageTitle from "@/components/shared/PageTitle.vue";
import FilterDrawer from "@/components/contacts/FilterDrawer.vue";
import { $contacts } from "@/services/Index";
import { mapGetters } from "vuex";
export default {
  name: "ContactView",
  components: {
    PageTitle,
    FilterDrawer,
  },
  data() {
    return {
      secondaryAction: null,
      breadcrumbsList: [
        {
          text: "Contacts",
          disabled: false,
          path: "",
        },
      ],
      loadFilter: false,
      headers: [
        { text: "Contact Name", align: "start", value: "full_name" },
        { text: "Email Address", value: "email", width: "400px" },
        { text: "Phone Number", value: "phone" },
        { text: "Company", value: "company" },
        { text: "Actions", value: "action", align: "right", sortable: false },
      ],
      contactList: [],
      perPage: 10,
      page: 1,
      totalItems: 0,
      pagination: {},
      loading: false,
      sortingToggle: false,
      selectedContact: {},
      deleteDialog: false,
      searchString: '',
    };
  },
  computed: {
    associatedField() {
      return this.$store.getters["contacts/associatedField"];
    },
    ...mapGetters({
      account: ["auth/account"],
      deletedContactToggle:["contacts/getdeletedContactToggle"]
    }),
    // associatedField() {
    //   return this.$store.getters["tickets/associatedField"];
    // },
  },
  mounted() {
    if (this.$route.query) {
      this.searchString = this.$route.query.name || "";
    }
    this.$store.dispatch("app/getData");
  },
  watch: {
    pagination: {
      handler(newVal, oldVal) {
        /** prevent this check to invoked function on page load */
        if (oldVal.itemsPerPage) {
          this.getContactList();
        }
      },
      deep: true,
    },

    /*** temporary code for global search */
    $route: {
      handler: function (to, from) {
        this.searchString = this.$route.query.name ? this.$route.query.name : '';
        this.pagination.page = 1;
        this.getContactList();
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    getRowData(item) {
      this.selectedContact = item;
    },

    /**
     * @description on call filter from filter drawer
     * set the pagination
     * fetch contact list
     */
    applyFilter(e) {
      this.$store.dispatch("contacts/deletedContactToggle", [e])
      this.pagination.page = 1
      this.getContactList()
    },

    /** get contact list data from API's */
    async getContactList() {
      this.loading = true;
      const { page, itemsPerPage, sortBy, sortDesc } = this.pagination;
      let appliedFilter = this.$store.getters["contacts/appliedFilter"];
      const data = {
        page,
        per_page: itemsPerPage,
        q: {
          first_name_or_last_name_or_email_or_phone_cont: this.searchString,
          m: "or",
        },
      };

      if (Object.keys(appliedFilter).length !== 0 ) {
        for (var key in appliedFilter) {
          data.q[key] = appliedFilter[key]
        }
        data.q["m"] = "and";
      }
      // Add or remove the email_does_not_match filter based on deletedContactToggle
      if (!this.deletedContactToggle) {
          data.q.email_does_not_match = "%@pseudonymize.email";
          data.q.m = "and"
        } else {
          delete data.q.email_does_not_match;
      }
      // If sorting .. 
      if (sortBy && sortBy.length) {
        const dir = sortDesc.length
          ? sortDesc[0] === true
            ? "desc"
            : "asc"
          : "";
        const sortByField = (sortBy[0] === 'full_name') ? 'first_name' : sortBy[0]
        data.q.s = `${sortByField} ${dir}`;
      }
      await $contacts
        .fetchList(data, {})
        .then((response) => {
          this.totalItems = response.data.pagination.total_count;
          this.contactList = response.data.contacts;
          this.loading = false;
          this.loadFilter = true;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    /** On click of delete button confirmDelete() function */
    confirmDelete() {
      this.deleteContact(this.selectedContact);
      this.deleteDialog = false;
    },

    /* eslint-disable */
    /** delete contact method */
    async deleteContact(data) {
      try {
        const response = await $contacts.delete(data.id, {});
        if (response.data) {
          this.$store.dispatch("ui/snackbar", [
            {
              title: "Delete Contact",
              message: "Contact has been deleted.",
              type: "success",
            },
          ]);
          this.getContactList();
        }
      } catch (error) {
        console.log("error===", error);
      }
    },
    checkPseudonymized (item) {
      // Other checks here
      return !item.email.includes('pseudonymize.email')
    },
  },
};
</script>
